import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { LogIn } from 'lucide-react';
import { useState, type FormEvent } from 'react';

import { ApiError, NetworkError } from '@/shared/api/http';
import { signIn, useSession } from '@/shared/auth';
import { t } from '@/shared/i18n';
import { Button } from '@/shared/ui/Button';
import { Field } from '@/shared/ui/Field';
import { Input } from '@/shared/ui/Input';

import styles from '../auth.module.scss';

/**
 * Вход в админку — карточка по центру, как во flow-do.
 */
export function LoginPage() {
  const session = useSession();
  const navigate = useNavigate();
  const { redirect } = useSearch({ from: '/public/login' });

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    setError(undefined);
    setBusy(true);

    signIn(email, password)
      .then(() => {
        // Возврат туда, откуда человека увели на вход: он шёл по ссылке на
        // конкретный экран, и высаживать его на главную — терять его цель.
        // Только внутренние адреса: во внешнем можно увести на чужой сайт.
        const target = redirect?.startsWith('/') === true ? redirect : '/';

        return navigate({ to: target });
      })
      .catch((cause: unknown) => {
        // Ошибка сервера и обрыв связи требуют от человека разных действий:
        // в первом случае проверить пароль, во втором — просто повторить.
        if (cause instanceof ApiError) {
          setError(cause.detail);
        } else if (cause instanceof NetworkError) {
          setError(t.login.networkError);
        } else {
          setError(t.login.failed);
        }
      })
      .finally(() => {
        setBusy(false);
      });
  };

  if (session.status === 'authenticated') {
    return (
      <div className={styles['card']}>
        <p className={styles['text']}>
          {t.login.already} <b>{session.user.email}</b>.
        </p>
        <Button variant="outline" tone="neutral" asChild>
          <Link to="/sites">{t.login.toSites}</Link>
        </Button>
      </div>
    );
  }

  return (
    <form className={styles['card']} onSubmit={handleSubmit} noValidate>
      <header className={styles['head']}>
        <h1 className={styles['title']}>{t.login.title}</h1>
        <p className={styles['text']}>{t.login.subtitle}</p>
      </header>

      <Field label={t.login.email} required>
        <Input
          type="email"
          name="email"
          autoComplete="username"
          placeholder="david@faceta.digital"
          value={email}
          onChange={(event) => {
            setEmail(event.target.value);
          }}
        />
      </Field>

      <Field
        label={t.login.password}
        required
        // Ошибка входа показывается у пароля, а не отдельным блоком: человек
        // смотрит туда, где последний раз печатал.
        error={error}
      >
        <Input
          type="password"
          name="password"
          autoComplete="current-password"
          value={password}
          onChange={(event) => {
            setPassword(event.target.value);
          }}
        />
      </Field>

      <Button type="submit" size="lg" fullWidth loading={busy} iconLeft={<LogIn size={16} />}>
        {t.login.submit}
      </Button>
    </form>
  );
}
